/**
 * "Used in lessons" slide-in panel (NA-198) — shown when an author clicks the
 * Question Bank list's "Used in" cell, or the edit panel's "Show X more"
 * overflow link. One shared right-side panel for both entry points (rather
 * than a centered dialog) per the mockup: header with the question text as
 * context, a list of lessons (title, "L{n}" id, publish-state badge), and
 * a footer noting removal isn't done from here plus the total count.
 */
import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { adminSidePanel } from '../../admin-ui';
import { formatLessonHumanId } from '../../utils/humanIds';
import { WorkflowStatusBadge } from '../WorkflowStatusBadge';
import type { QuestionUsage, QuestionUsageLesson } from '../../api/questionApi';

export interface QuestionUsagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shown as context under the panel title — the question this usage belongs to. */
  questionText: string;
  usage: QuestionUsage;
}

/**
 * NA-390: a base usage and an account-override usage of the same lesson are
 * separate entries sharing one `lessonId` — label and key each independently
 * so they never merge or collide. Mirrors ImageLibraryPage's
 * formatUsageReference/usageReferenceKey.
 */
function formatUsageLesson(lesson: QuestionUsageLesson): string {
  return lesson.accountName ? `${lesson.title} (${lesson.accountName})` : lesson.title;
}

function usageLessonKey(lesson: QuestionUsageLesson): string {
  return `${lesson.lessonId}-${lesson.externalAccountId ?? 'base'}`;
}

export function QuestionUsagePanel({
  isOpen,
  onClose,
  questionText,
  usage,
}: QuestionUsagePanelProps): React.ReactElement {

  const styles = useMemo((): Record<string, CSSProperties> => ({
    ...adminSidePanel,
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 1200,
      display: 'flex',
      justifyContent: 'flex-end',
      pointerEvents: isOpen ? 'auto' : 'none',
    },
    scrim: {
      ...adminSidePanel.scrim,
      opacity: isOpen ? 1 : 0,
      transition: 'opacity 0.2s ease',
    },
    panel: {
      ...adminSidePanel.panel,
      position: 'relative',
      width: '420px',
      maxWidth: '100%',
      height: '100%',
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.2s ease',
    },
  }), [isOpen]);

  return (
    <div style={styles.overlay} aria-hidden={!isOpen}>
      <div style={styles.scrim} onClick={onClose} role="presentation" />
      <aside
        style={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="question-usage-panel-title"
      >
        <div style={styles.header}>
          <div style={styles.headerText}>
            <h2 id="question-usage-panel-title" style={styles.title}>Used in lessons</h2>
            <p style={styles.subtitle}>{questionText}</p>
          </div>
          <button type="button" style={styles.closeButton} onClick={onClose} aria-label="Close panel">
            ×
          </button>
        </div>

        <div style={styles.body}>
          {usage.lessons.map((lesson) => (
            <div key={usageLessonKey(lesson)} style={styles.row}>
              <div style={styles.rowText}>
                <Link to={`/lessons/${lesson.lessonId}`} style={styles.rowLink}>
                  {formatUsageLesson(lesson)}
                </Link>
                <p style={styles.rowMeta}>{formatLessonHumanId(lesson.lessonNumericId)}</p>
              </div>
              <WorkflowStatusBadge state={lesson.workflowState} />
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          <p style={styles.footerNote}>To remove this question from a lesson, edit the lesson directly.</p>
          <span style={styles.footerCount}>{usage.count} {usage.count === 1 ? 'lesson' : 'lessons'}</span>
        </div>
      </aside>
    </div>
  );
}

QuestionUsagePanel.displayName = 'QuestionUsagePanel';
